import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosClient, getBaseUrlVersioned } from "../../services/axiosClient";
import axios from "axios";
import { ABORT_ERROR_MESSAGE } from "../conversation";
import { API_VERSION } from "../types";
import { CharacterFlow } from "./types";
const API_PATH_CHARACTER = "characters";
const API_PATH_CHARACTER_TRANSLATION = "characters/translation";
const API_PATH_CHARACTER_REPORT = "reportCharacter";
export const getCharacterById = createAsyncThunk("character/getById", async ({ id, language, include }, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V2)}/${API_PATH_CHARACTER}/${id}`;
        const params = {
            ...(language ? { language } : {}),
            ...(include?.length ? { include: include.join(",") } : {}),
        };
        const response = await axiosClient.get(apiUrl, { params });
        return response.data;
    }
    catch (err) {
        throw thunkAPI.rejectWithValue(err?.response?.data);
    }
});
export const createCharacter = createAsyncThunk("character/create", async (params, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V2)}/${API_PATH_CHARACTER}?switch=T1`;
        const response = await axiosClient.post(apiUrl, params);
        return response.data;
    }
    catch (err) {
        throw thunkAPI.rejectWithValue(err?.response?.data);
    }
});
export const updateCharacter = createAsyncThunk("character/update", async ({ id, params }, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V2)}/${API_PATH_CHARACTER}/${id}?switch=T1`;
        const response = await axiosClient.patch(apiUrl, params);
        return response.data;
    }
    catch (err) {
        throw thunkAPI.rejectWithValue(err?.response?.data);
    }
});
export const generateAvatarImageThunk = createAsyncThunk("generate-image", async ({ prompt, negative_prompt, seed, image, model_style, prompt_image_style, abortSignal, apiVersion, }, thunkAPI) => {
    try {
        const response = await axiosClient.post(`${getBaseUrlVersioned(apiVersion || null)}/generate-image`, {
            prompt,
            negative_prompt,
            seed,
            image,
            model_style,
            prompt_image_style,
        }, { signal: abortSignal });
        return response.data;
    }
    catch (err) {
        if (axios.isCancel(err)) {
            throw new Error(ABORT_ERROR_MESSAGE);
        }
        throw thunkAPI.rejectWithValue(err?.response?.data);
    }
});
export const editAvatarImageThunk = createAsyncThunk("edit-image", async ({ prompt, model, seed, referenceImage, abortSignal, }, thunkAPI) => {
    try {
        const response = await axiosClient.post(`${getBaseUrlVersioned(API_VERSION.V3)}/edit-image`, { prompt, model, seed, reference_image: referenceImage }, { signal: abortSignal });
        return response.data;
    }
    catch (err) {
        if (axios.isCancel(err)) {
            throw new Error(ABORT_ERROR_MESSAGE);
        }
        throw thunkAPI.rejectWithValue(err?.response?.data);
    }
});
export const saveAvatarImageThunk = createAsyncThunk("save-image", async ({ prompt, image_type, hash, fileSize, imageData, generation_ref }, thunkAPI) => {
    try {
        const response = await axiosClient.post(`${getBaseUrlVersioned(null)}/save-image`, { prompt, image_type, hash, fileSize, imageData, generation_ref });
        return response.data;
    }
    catch (err) {
        throw thunkAPI.rejectWithValue(err?.response?.data);
    }
});
export const createCharacterTranslation = createAsyncThunk("character/createTranslation", async (params, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V2)}/${API_PATH_CHARACTER_TRANSLATION}`;
        const response = await axiosClient.post(apiUrl, params);
        return response.data;
    }
    catch (err) {
        throw thunkAPI.rejectWithValue(err?.response?.data);
    }
});
export const getCharacterTranslation = createAsyncThunk("character/getTranslation", async ({ character_id, target_language }, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V2)}/${API_PATH_CHARACTER_TRANSLATION}/${character_id}/${target_language}`;
        const response = await axiosClient.get(apiUrl);
        return response.data;
    }
    catch (err) {
        throw thunkAPI.rejectWithValue(err?.response?.data);
    }
});
export const suggestCharacterTags = createAsyncThunk("characters/suggest_tags", async (characterFields, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(null)}/characters/suggest_tags`;
        const response = await axiosClient.post(apiUrl, characterFields);
        return response.data;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const reportCharacter = createAsyncThunk("character/report", async (params, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_CHARACTER_REPORT}`;
        const response = await axiosClient.post(apiUrl, params);
        return response.data;
    }
    catch (err) {
        throw thunkAPI.rejectWithValue(err?.response?.data);
    }
});
export const suggestCharacterField = createAsyncThunk("characters/suggest_field", async (body, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(null)}/characters/suggest_field`;
        const response = await axiosClient.post(apiUrl, body);
        return response.data;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
const initialState = {
    loading: false,
    loadingType: null,
    characterData: null,
};
const handlePending = (state, loadingType) => {
    state.loading = true;
    state.loadingType = loadingType;
};
const handleFulfilled = (state) => {
    state.loading = false;
    state.loadingType = null;
};
const handleRejected = (state) => {
    state.loading = false;
    state.loadingType = null;
};
export const characterSlice = createSlice({
    name: "character",
    initialState: initialState,
    reducers: {
        clearLoading: (state) => {
            state.loading = false;
            state.loadingType = null;
        },
        setCharacterData: (state, action) => {
            state.characterData = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getCharacterById.pending, (state) => {
            handlePending(state, CharacterFlow.GET_CHARACTER);
        });
        builder.addCase(getCharacterById.fulfilled, (state, action) => {
            handleFulfilled(state);
            state.characterData = action.payload;
        });
        builder.addCase(getCharacterById.rejected, (state) => {
            handleRejected(state);
        });
        builder.addCase(createCharacter.pending, (state) => {
            handlePending(state, CharacterFlow.CREATE_CHARACTER);
        });
        builder.addCase(createCharacter.fulfilled, (state, action) => {
            handleFulfilled(state);
            state.characterData = action.payload;
        });
        builder.addCase(createCharacter.rejected, (state) => {
            handleRejected(state);
        });
        builder.addCase(updateCharacter.pending, (state) => {
            handlePending(state, CharacterFlow.UPDATE_CHARACTER);
        });
        builder.addCase(updateCharacter.fulfilled, (state, action) => {
            handleFulfilled(state);
            state.characterData = action.payload;
        });
        builder.addCase(updateCharacter.rejected, (state) => {
            handleRejected(state);
        });
        builder.addCase(generateAvatarImageThunk.pending, (state) => {
            handlePending(state, CharacterFlow.GENERATE_AVATAR_IMAGE);
        });
        builder.addCase(generateAvatarImageThunk.fulfilled, (state) => {
            handleFulfilled(state);
        });
        builder.addCase(generateAvatarImageThunk.rejected, (state) => {
            handleRejected(state);
        });
        builder.addCase(editAvatarImageThunk.pending, (state) => {
            handlePending(state, CharacterFlow.EDIT_AVATAR_IMAGE);
        });
        builder.addCase(editAvatarImageThunk.fulfilled, (state) => {
            handleFulfilled(state);
        });
        builder.addCase(editAvatarImageThunk.rejected, (state) => {
            handleRejected(state);
        });
        builder.addCase(saveAvatarImageThunk.pending, (state) => {
            handlePending(state, CharacterFlow.SAVE_AVATAR_IMAGE);
        });
        builder.addCase(saveAvatarImageThunk.fulfilled, (state) => {
            handleFulfilled(state);
        });
        builder.addCase(saveAvatarImageThunk.rejected, (state) => {
            handleRejected(state);
        });
        builder.addCase(createCharacterTranslation.pending, (state) => {
            handlePending(state, CharacterFlow.CREATE_TRANSLATION);
        });
        builder.addCase(createCharacterTranslation.fulfilled, (state) => {
            handleFulfilled(state);
        });
        builder.addCase(createCharacterTranslation.rejected, (state) => {
            handleRejected(state);
        });
        builder.addCase(suggestCharacterTags.fulfilled, (state) => {
            handleFulfilled(state);
        });
        builder.addCase(suggestCharacterTags.pending, (state) => {
            handlePending(state, CharacterFlow.SUGGEST_TAGS);
        });
        builder.addCase(suggestCharacterTags.rejected, (state) => {
            handleRejected(state);
        });
        builder.addCase(getCharacterTranslation.pending, (state) => {
            handlePending(state, CharacterFlow.GET_TRANSLATION);
        });
        builder.addCase(getCharacterTranslation.fulfilled, (state) => {
            handleFulfilled(state);
        });
        builder.addCase(getCharacterTranslation.rejected, (state) => {
            handleRejected(state);
        });
        builder.addCase(reportCharacter.pending, (state) => {
            handlePending(state, CharacterFlow.REPORT_CHARACTER);
        });
        builder.addCase(reportCharacter.fulfilled, (state) => {
            handleFulfilled(state);
        });
        builder.addCase(reportCharacter.rejected, (state) => {
            handleRejected(state);
        });
    },
});
export const { clearLoading, setCharacterData } = characterSlice.actions;
export default characterSlice.reducer;
